import { SignUpButton } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  LucideChevronLeft,
  LucideChevronRight,
  LucideCheck,
  LucideX,
  LucideUsers,
  LucideSettings2,
  LucideDownload,
} from "lucide-react";
import { pdfjs } from "react-pdf";
import { PDFPreview } from "./PDFPreview";
import { ReportFormHeader } from "./ReportFormHeader";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

/**
 * Shared layout shell for all report form pages.
 *
 * Renders the header bar, shared/defaults banners, step indicator,
 * preset toolbar, form content area, navigation buttons, PDF preview panel,
 * and the free-trial post-download CTA modal.
 *
 * Props:
 *   hook              - Return value from useReportForm()
 *   title             - Report card title (e.g. "Ontario Progress Report Card")
 *   reportId          - Report ID (null for free trial)
 *   backUrl           - URL for the Back button
 *   contextInfo       - { grade, schoolYear, oen, studentName }
 *   defaultsGroupId   - Group ID when editing class defaults
 *   onRequestSave     - Callback for "Save Defaults" button
 *   STEPS             - Step definitions array
 *   PRESETS            - Preset data array for free trial toolbar
 *   defaultPageCount  - Number of PDF pages (for preview skeleton)
 *   StepIndicator     - Step indicator component
 *   stepIndicatorProps - Extra props to pass to StepIndicator
 *   renderStep        - (form, currentStep) => JSX for the active step content
 *   formKey           - Optional key on the <form> element (used by provincial reports)
 *   formContentClassName - Optional className for the step content scroll area
 */
export function ReportFormShell({
  hook,
  title,
  reportId,
  backUrl,
  contextInfo,
  defaultsGroupId,
  onRequestSave,
  STEPS,
  PRESETS,
  defaultPageCount = 2,
  StepIndicator,
  stepIndicatorProps = {},
  renderStep,
  formKey,
  formContentClassName = "flex-1 overflow-y-auto px-6 pb-4",
  readOnly = false,
}) {
  const navigate = useNavigate();
  const {
    form,
    currentStep,
    setCurrentStep,
    filledPDF,
    isPdfGenerating,
    showPreview,
    setShowPreview,
    showPostDownloadModal,
    setShowPostDownloadModal,
    isDirty,
    isSaving,
    hasSavedOnce,
    scrollTargets,
    completedSteps,
    formPaneRef,
    handleNext,
    handlePrev,
    handleDownload,
    toggleManualComplete,
    triggerPdfRegen,
    flushSaveRef,
  } = hook;

  const isShared = backUrl?.startsWith("/shared-classes");

  // Only show save status for persisted reports (not free trial or defaults mode)
  const saveStatus = reportId
    ? (isSaving || isDirty ? "saving" : hasSavedOnce.current ? "saved" : null)
    : null;

  function handleBack() {
    if (isDirty && reportId) {
      if (!window.confirm("You have unsaved changes. Leave anyway?")) return;
    }
    flushSaveRef.current?.();
    navigate(backUrl || "/");
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col font-sans">
      {/* Header bar */}
      <ReportFormHeader
        onBack={handleBack}
        title={title}
        saveStatus={saveStatus}
        contextInfo={contextInfo}
        showPreview={showPreview}
        onTogglePreview={() => setShowPreview((v) => !v)}
        isPdfGenerating={isPdfGenerating}
        filledPDF={filledPDF}
        onDownload={handleDownload}
        defaultsGroupId={defaultsGroupId}
        onSaveDefaults={() => onRequestSave?.(form.getValues())}
      />

      {/* Shared-class banner */}
      {isShared && (
        <div className="flex items-center gap-2 px-5 py-1.5 bg-[hsl(var(--warning-bg))] border-b border-[hsl(var(--warning-border))] text-xs text-[hsl(var(--warning-fg))] shrink-0">
          <LucideUsers size={12} className="shrink-0" />
          {readOnly
            ? "View only - you can view and download this report but cannot make changes."
            : "Shared class - changes you save are visible to the owner"}
        </div>
      )}

      {/* Class Defaults banner */}
      {defaultsGroupId && (
        <div className="flex items-center gap-2 px-5 py-2.5 bg-[hsl(var(--info-bg))] border-b-2 border-[hsl(var(--info-border))] shrink-0">
          <LucideSettings2 size={15} className="shrink-0 text-[hsl(var(--info-fg))]" />
          <span className="text-sm font-medium text-[hsl(var(--info-fg))]">
            Class Defaults - <span className="font-normal">You&apos;re setting default values for all reports in this class. No individual student is being edited.</span>
          </span>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Form panel */}
        <div className={`flex flex-col overflow-hidden transition-all duration-300 ${showPreview ? "w-[55%]" : "w-full max-w-4xl mx-auto"}`}>
          {/* Step indicator */}
          <div className="px-6 pt-5 pb-3 shrink-0">
            <StepIndicator
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={setCurrentStep}
              {...stepIndicatorProps}
            />
          </div>

          {/* Free-trial preset toolbar */}
          {!reportId && !defaultsGroupId && PRESETS && PRESETS.length > 0 && (
            <div className="px-6 py-2 shrink-0 flex flex-wrap gap-2 border-b border-border bg-muted/50">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs transition-all duration-150 hover:scale-105 hover:shadow-md active:scale-95"
                  onClick={() => { form.reset(preset.data); triggerPdfRegen(); }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          )}

          {/* Step content */}
          <div ref={formPaneRef} className={formContentClassName}>
            <div
              inert={readOnly ? "" : undefined}
              style={readOnly ? { opacity: 0.75, pointerEvents: "none" } : undefined}
            >
              <Form {...form}>
                <form key={formKey}>
                  {renderStep(form, currentStep)}
                </form>
              </Form>
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between px-6 py-3 border-t border-border bg-card shrink-0">
            <Button type="button" variant="outline" onClick={handlePrev} disabled={currentStep === 1}>
              <LucideChevronLeft size={16} className="mr-1" />
              Prev
            </Button>
            <div className="flex items-center gap-2">
              {!isShared && !readOnly && (
                <Button
                  type="button"
                  size="sm"
                  variant={completedSteps.has(currentStep) ? "default" : "outline"}
                  onClick={() => toggleManualComplete(currentStep)}
                  className={completedSteps.has(currentStep) ? "gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600" : "gap-1.5"}
                >
                  <LucideCheck size={14} />
                  {completedSteps.has(currentStep) ? "Marked Complete" : "Mark Complete"}
                </Button>
              )}
              {currentStep < STEPS.length ? (
                <Button type="button" onClick={handleNext}>
                  Next
                  <LucideChevronRight size={16} className="ml-1" />
                </Button>
              ) : (
                <Button type="button" onClick={handleDownload} disabled={!filledPDF || isPdfGenerating}>
                  <LucideDownload size={16} className="mr-1.5" />
                  {isPdfGenerating ? "Updating…" : "Download PDF"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* PDF preview panel */}
        <div className={`border-l border-border transition-all duration-300 overflow-hidden ${showPreview ? "w-[45%]" : "w-0"}`}>
          {showPreview && (
            <PDFPreview
              src={filledPDF}
              scrollTarget={scrollTargets[currentStep]}
              defaultPageCount={defaultPageCount}
            />
          )}
        </div>
      </div>

      {/* Free-trial CTA overlay */}
      {showPostDownloadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="relative bg-card rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden">
            <div className="p-6 pt-5">
              <button
                onClick={() => setShowPostDownloadModal(false)}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close"
              >
                <LucideX size={16} />
              </button>
              <h2
                className="text-xl font-semibold text-foreground leading-snug mb-4 font-display"
              >
                Download Complete! <br /> Take a look :)
              </h2>
              <div className="border-t border-border mb-4" />
              <SignUpButton mode="redirect">
                <button
                  className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white transition-all duration-150 hover:opacity-90 active:scale-[0.98] bg-gradient-to-br from-[hsl(var(--brand-900))] to-[hsl(var(--brand-700))]"
                >
                  Create free account
                </button>
              </SignUpButton>
              <ul className="mt-3 space-y-1.5">
                {[
                  "Collaborate with teachers to complete reports",
                  "Use Gen AI to support your comments",
                  "Automate dozens of fields - no manual typing",
                  "And much more\u2026",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <span className="mt-0.5 shrink-0 w-3.5 h-3.5 rounded-full bg-primary/5 flex items-center justify-center">
                      <LucideCheck size={8} className="text-primary" strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
